import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Newspaper, Plus, Loader2, Trash2 } from 'lucide-react';
import axios from 'axios';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function NewsPage() {
  const { isAdmin, token } = useAuth();
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [form, setForm] = useState({ title: '', content: '', category: 'general', pinned: false });

  useEffect(() => {
    if (token) {
      fetchNews();
    }
  }, [token]);

  const fetchNews = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/news`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNews(res.data);
    } catch (error) {
      console.error('Error fetching news:', error);
      toast.error('Erreur lors du chargement des actualités');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.title || !form.content) {
      toast.error('Veuillez remplir les champs obligatoires');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/news`, form, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Actualité publiée ! Les utilisateurs ont été notifiés.');
      setDialogOpen(false);
      setForm({ title: '', content: '', category: 'general', pinned: false });
      fetchNews();
    } catch (error) {
      console.error('Error creating news:', error);
      toast.error('Erreur lors de la publication');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette actualité ?')) return;
    setDeleting(id);
    try {
      await axios.delete(`${API_URL}/api/news/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Actualité supprimée');
      fetchNews();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    } finally {
      setDeleting(null);
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="news-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Actualités</h1>
          <p className="text-slate-500 mt-1">Dernières nouvelles de l'entreprise</p>
        </div>
        {isAdmin && (
          <Button onClick={() => setDialogOpen(true)} className="bg-slate-900 hover:bg-slate-800">
            <Plus className="w-4 h-4 mr-2" />Publier
          </Button>
        )}
      </div>

      {news.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-slate-400">
            <Newspaper className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>Aucune actualité</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-6">
          {news.map((item) => (
            <Card key={item.id} className="overflow-hidden">
              <CardContent className="p-6">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-3">
                      <Badge variant="secondary">{item.category}</Badge>
                      {item.pinned && <Badge className="bg-amber-100 text-amber-700">Épinglé</Badge>}
                    </div>
                    <h2 className="text-xl font-bold mb-2">{item.title}</h2>
                    <p className="text-slate-600 whitespace-pre-wrap">{item.content}</p>
                    <div className="flex items-center gap-2 mt-4 text-sm text-slate-400">
                      <span>Par {item.author_name}</span>
                      <span>•</span>
                      <span>{format(new Date(item.created_at), 'dd MMMM yyyy', { locale: fr })}</span>
                    </div>
                  </div>
                  {isAdmin && (
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      onClick={() => handleDelete(item.id)}
                      disabled={deleting === item.id}
                      data-testid={`delete-news-${item.id}`}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    >
                      {deleting === item.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Trash2 className="w-4 h-4" />
                      )}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Nouvelle actualité</DialogTitle></DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="space-y-2">
              <Label>Titre *</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Contenu *</Label>
              <Textarea value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} rows={5} />
            </div>
            <div className="space-y-2">
              <Label>Catégorie</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="general">Général</SelectItem>
                  <SelectItem value="announcement">Annonce</SelectItem>
                  <SelectItem value="update">Mise à jour</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button>
              <Button type="submit" disabled={submitting} className="bg-slate-900 hover:bg-slate-800">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Publier'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
